/**
 * CharacterPickerModal
 *
 * Shown during enrollment when character_archetypes_enabled is true for a domain.
 * Lets the learner choose their learning path (character archetype) before entering.
 *
 * Props:
 *   domain               - string  — domain ID being enrolled in
 *   characterClasses     - array   — [{ id, display_name, description, icon }, ...]
 *   characterDisplayName - string  — label from domain config (e.g. "Role", "Track")
 *   onSelect(classId)    - async fn called when learner picks a class
 *   onSkip()             - called when learner skips (first class used as default)
 */

import React, { useState } from 'react';

export default function CharacterPickerModal({
  domain,
  characterClasses = [],
  characterDisplayName = 'Learning Path',
  onSelect,
  onSkip,
}) {
  const [selecting, setSelecting] = useState(null);

  const handleSelect = async (cls) => {
    const classId = cls.id || cls.class_id || cls.label;
    setSelecting(classId);
    try {
      await onSelect(classId);
    } finally {
      setSelecting(null);
    }
  };

  const handleSkip = () => {
    const defaultClass = characterClasses[0];
    onSkip(defaultClass?.id || defaultClass?.class_id || null);
  };

  return (
    <div className="cpm-overlay" role="dialog" aria-modal="true" aria-labelledby="cpm-title">
      <div className="cpm-modal">
        <div className="cpm-header">
          <h2 id="cpm-title" className="cpm-title">Choose Your {characterDisplayName}</h2>
          <p className="cpm-subtitle">
            Select the path that best matches your goals for{' '}
            <strong>{domain}</strong>.{' '}
            You can change this later from your profile.
          </p>
        </div>

        <div className="cpm-cards">
          {characterClasses.map((cls) => {
            const classId = cls.id || cls.class_id;
            const displayName = cls.display_name || cls.label || classId;
            const description = cls.description || '';
            const icon = cls.icon || '🎯';
            const isSelecting = selecting === (cls.id || cls.class_id || cls.label);

            return (
              <div key={classId} className="cpm-card">
                <div className="cpm-card-icon" aria-hidden="true">{icon}</div>
                <h3 className="cpm-card-name">{displayName}</h3>
                {description && (
                  <p className="cpm-card-desc">{description}</p>
                )}
                <button
                  className="cpm-select-btn"
                  onClick={() => handleSelect(cls)}
                  disabled={selecting !== null}
                >
                  {isSelecting ? 'Selecting…' : 'Select'}
                </button>
              </div>
            );
          })}
        </div>

        <div className="cpm-footer">
          <button className="cpm-skip-btn" onClick={handleSkip} disabled={selecting !== null}>
            Skip for now
          </button>
          {characterClasses[0] && (
            <span className="cpm-skip-note">
              (defaults to:{' '}
              {characterClasses[0].display_name || characterClasses[0].label || characterClasses[0].id})
            </span>
          )}
        </div>
      </div>

      <style>{`
        .cpm-overlay {
          position: fixed; inset: 0;
          background: rgba(0, 0, 0, 0.75);
          display: flex; align-items: center; justify-content: center;
          z-index: 1000; padding: 1rem;
          animation: cpm-fade-in 0.18s ease;
        }
        @keyframes cpm-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .cpm-modal {
          background: #1e293b;
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 1.25rem;
          padding: 2rem;
          max-width: 760px; width: 100%;
          max-height: 90vh; overflow-y: auto;
          display: flex; flex-direction: column; gap: 1.75rem;
          animation: cpm-slide-up 0.2s ease;
        }
        @keyframes cpm-slide-up {
          from { transform: translateY(16px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        .cpm-header { text-align: center; }
        .cpm-title  { margin: 0 0 0.5rem; font-size: 1.5rem; color: #e2e8f0; }
        .cpm-subtitle { color: #94a3b8; font-size: 0.875rem; margin: 0; line-height: 1.5; }

        .cpm-cards {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 1rem;
        }
        .cpm-card {
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 1rem; padding: 1.5rem;
          display: flex; flex-direction: column; align-items: center;
          gap: 0.6rem; text-align: center;
          transition: border-color 0.2s, background 0.2s;
        }
        .cpm-card:hover { border-color: #6366f1; background: rgba(99, 102, 241, 0.06); }
        .cpm-card-icon  { font-size: 2.5rem; line-height: 1; }
        .cpm-card-name  { margin: 0; font-size: 1.05rem; font-weight: 700; color: #e2e8f0; }
        .cpm-card-desc  {
          margin: 0; font-size: 0.8rem; color: #94a3b8;
          line-height: 1.5; flex: 1;
        }
        .cpm-select-btn {
          margin-top: 0.5rem;
          padding: 0.45rem 1.25rem;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          color: #fff; border: none; border-radius: 0.5rem;
          cursor: pointer; font-size: 0.875rem; font-weight: 600;
          transition: opacity 0.2s;
        }
        .cpm-select-btn:hover:not(:disabled) { opacity: 0.85; }
        .cpm-select-btn:disabled { opacity: 0.5; cursor: not-allowed; }

        .cpm-footer {
          display: flex; align-items: center; justify-content: center;
          gap: 0.75rem; flex-wrap: wrap;
          padding-top: 0.25rem;
          border-top: 1px solid rgba(255, 255, 255, 0.08);
        }
        .cpm-skip-btn {
          background: none;
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: #94a3b8; padding: 0.4rem 1rem;
          border-radius: 0.5rem; cursor: pointer; font-size: 0.85rem;
          transition: border-color 0.15s, color 0.15s;
        }
        .cpm-skip-btn:hover:not(:disabled) { border-color: rgba(255,255,255,0.4); color: #e2e8f0; }
        .cpm-skip-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .cpm-skip-note { font-size: 0.75rem; color: #64748b; }
      `}</style>
    </div>
  );
}
